import { readMealPlanRunTag } from "./mealPlanChatContext";

/**
 * "Save & commit this week" in chat. Issue #1452.
 *
 * She should not have to know to TELL Kin to save the plan (#1451's
 * commit_meal_plan tool stays as the spoken fallback). While a meal plan draft
 * is the subject of the conversation, a bar sits above the composer with one
 * button that does exactly what the card's Approve does.
 *
 * Pure state so it is testable without mounting Index.tsx.
 */

export interface MealPlanCardState {
  runId: string;
  committed: boolean;
  /** The last change Kin wrote through update_meal_plan_night, for the bar's line. */
  lastChange: string | null;
}

/** The plan this thread is about: the LAST user message carrying the #1450 marker. */
export function activeMealPlanRunFromMessages(
  messages: ReadonlyArray<{ role: string; content: string }>,
): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "user") continue;
    const id = readMealPlanRunTag(m.content);
    if (id) return id;
  }
  return null;
}

export type MealPlanCardEvent =
  | { type: "thread"; runId: string | null }
  | { type: "night_updated"; runId: string; day: string; mealType: string; mealName: string | null; removed: boolean }
  | { type: "committed"; runId: string }
  | { type: "reset" };

/**
 * One reducer for every way the bar learns something. A different run replaces
 * the state; the same run accumulates. `reset` is a new chat.
 */
export function reduceMealPlanCard(state: MealPlanCardState | null, ev: MealPlanCardEvent): MealPlanCardState | null {
  switch (ev.type) {
    case "reset":
      return null;
    case "thread":
      if (!ev.runId) return state;
      if (state && state.runId === ev.runId) return state;
      return { runId: ev.runId, committed: false, lastChange: null };
    case "night_updated": {
      const base = state && state.runId === ev.runId ? state : { runId: ev.runId, committed: false, lastChange: null };
      const label = ev.mealType && ev.mealType !== "dinner" ? `${ev.day} ${mealTypeWord(ev.mealType)}` : ev.day;
      return { ...base, lastChange: ev.removed ? `${label}: no plan that night` : `${label}: ${ev.mealName || "updated"}` };
    }
    case "committed": {
      const base = state && state.runId === ev.runId ? state : { runId: ev.runId, committed: false, lastChange: null };
      return { ...base, committed: true };
    }
  }
}

function mealTypeWord(t: string): string {
  return t === "my_lunch" ? "lunch" : t === "school_lunch" ? "school lunch" : t;
}

export const MEAL_PLAN_CARD_COPY = {
  title: "This week's meal plan",
  subtitle: "Changes you agree on with Kin land on the card right away. Save when the week looks right.",
  save: "Save & commit this week",
  saving: "Saving…",
  saved: "Committed ✓",
  savedLine: "Saved. Kin will update your grocery list within a few minutes.",
  open: "Open the plan",
  failed: "Couldn't save that plan — try again in a sec 💜",
  alreadySet: "That week is already set.",
} as const;
